import WebsiteRepositoryInterface, {
  WebsiteCreation,
  WebsiteDeletion,
  WebsiteOrchestratorResourceType,
} from '@/services/Website/interfaces';
import { WebsiteMigration } from '@/services/WebsiteMigrations/interfaces';
import { sleep } from '@/utils/websiteQuickCreate';

export const ORCHESTRATOR_DELAY_DEFAULT_MS = 100;
export const ORCHESTRATOR_DELAY_OPTIONS_MS: number[] = [50, 100, 200, 500, 750, 1000];

export interface OrchestratorRowState {
  loading: boolean;
  creation: WebsiteCreation | null;
  deletion: WebsiteDeletion | null;
}

export function collectRelatedWebsiteIds(
  creations: WebsiteCreation[],
  deletions: WebsiteDeletion[],
  migrations: WebsiteMigration[],
): number[] {
  const ids: { [id: number]: true } = {};
  creations.forEach((item) => {
    if (item.websiteId) {
      ids[item.websiteId] = true;
    }
  });
  deletions.forEach((item) => {
    if (item.websiteId) {
      ids[item.websiteId] = true;
    }
  });
  migrations.forEach((item) => {
    if (item.sourceWebsiteId) {
      ids[item.sourceWebsiteId] = true;
    }
    if (item.targetWebsiteId) {
      ids[item.targetWebsiteId] = true;
    }
  });
  return Object.keys(ids).map((id) => Number(id)).filter((id) => id > 0).sort((a, b) => a - b);
}

export async function hydrateCreation(
  websiteRepository: WebsiteRepositoryInterface,
  websiteId: number,
  creation: WebsiteCreation,
): Promise<WebsiteCreation> {
  if (creation.actions && creation.actions.length) {
    return creation;
  }

  const detail = await websiteRepository.getWebsiteOrchestratorResource(
    websiteId,
    creation.id,
    WebsiteOrchestratorResourceType.CREATION,
  );
  return detail.creation || creation;
}

export async function hydrateDeletion(
  websiteRepository: WebsiteRepositoryInterface,
  websiteId: number,
  deletion: WebsiteDeletion,
): Promise<WebsiteDeletion> {
  if (deletion.actions && deletion.actions.length) {
    return deletion;
  }

  const detail = await websiteRepository.getWebsiteOrchestratorResource(
    websiteId,
    deletion.id,
    WebsiteOrchestratorResourceType.DELETION,
  );
  return detail.deletion || deletion;
}

export async function fetchLatestOrchestrator(
  websiteRepository: WebsiteRepositoryInterface,
  websiteId: number,
  retry: boolean = false,
): Promise<{ creation: WebsiteCreation | null; deletion: WebsiteDeletion | null }> {
  const attempts = retry ? 3 : 1;
  let creation: WebsiteCreation | null = null;
  let deletion: WebsiteDeletion | null = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const resources = await websiteRepository.getWebsiteOrchestratorResources(websiteId);
    const creations = resources.creations || [];
    const deletions = resources.deletions || [];

    if (creations.length) {
      const latestCreation = creations.reduce((best, current) => (
        current.id > best.id ? current : best
      ));
      creation = await hydrateCreation(websiteRepository, websiteId, latestCreation);
    }

    if (deletions.length) {
      const latestDeletion = deletions.reduce((best, current) => (
        current.id > best.id ? current : best
      ));
      deletion = await hydrateDeletion(websiteRepository, websiteId, latestDeletion);
    }

    if (creation || deletion || attempt >= attempts - 1) {
      break;
    }

    await sleep(400);
  }

  return { creation, deletion };
}

/**
 * Build latest creation/deletion from an already-loaded orchestrator payload
 * for one website (avoids a duplicate list fetch for the searched site).
 */
export async function latestFromLoadedResources(
  websiteRepository: WebsiteRepositoryInterface,
  websiteId: number,
  creations: WebsiteCreation[],
  deletions: WebsiteDeletion[],
): Promise<{ creation: WebsiteCreation | null; deletion: WebsiteDeletion | null }> {
  const siteCreations = creations.filter((item) => item.websiteId === websiteId);
  const siteDeletions = deletions.filter((item) => item.websiteId === websiteId);

  let creation: WebsiteCreation | null = null;
  let deletion: WebsiteDeletion | null = null;

  if (siteCreations.length) {
    const latestCreation = siteCreations.reduce((best, current) => (
      current.id > best.id ? current : best
    ));
    creation = await hydrateCreation(websiteRepository, websiteId, latestCreation);
  }

  if (siteDeletions.length) {
    const latestDeletion = siteDeletions.reduce((best, current) => (
      current.id > best.id ? current : best
    ));
    deletion = await hydrateDeletion(websiteRepository, websiteId, latestDeletion);
  }

  return { creation, deletion };
}
